import React from 'react';
import pick from 'lodash/pick';
import classnames from 'classnames';
import { Link } from 'react-router';
import { Field, Form, submit } from 'redux-form';
import * as Sentry from '@sentry/browser';
import PAPBase from 'pap/containers/common/pap_base';
import PAPQuestionPage from 'pap/components/common/pap_question_base';
import {
  CheckBoxField, SelectField, TextInputField, PhoneInputField, TextAreaField
} from 'common/components/form_fields';
import { LoadingIndicator } from 'common/components/preloader';
import BraintreeForm from 'common/containers/braintree_form';
import PaymentMethod from 'common/containers/payment_method';
import AnimatedButton from 'common/components/animated_button';
import { getDetailedCost } from 'pap/utils';
import CostBreakdown from 'pap/components/common/cost_breakdown';
import Money from 'common/components/money';
import { getProjectUrl } from 'project/utils';
import { getSubcategorySlug, getCategory } from 'common/ducks/categories';
import { required, requiredConfirmation, phone } from 'pap/components/common/input_fields';
import {
  trackGoogle, trackPerfectAudience, trackFacebookPurchase, trackBing, trackQuora,
  trackFacebookCustomEvent, trackAdWordsProjectPostedConversion,
  setMixpanelProfile, mixpanelProfileFromCsProfile,
  trackMixpanel, trackClickWithMixpanel, trackChargeWithMixpanel,
  MP_CHANGED_PAP_STEP_EVENT_NAME, MP_CHANGED_PAP_SECTION_EVENT_NAME
} from 'common/tracking';


/**
 * The final step of the PAP process - payment.
 *
 * @author Kamil Grymuza <kamil@startupfoundry.com>
 */
export default class PAPPayment extends React.Component {
  constructor(props) {
    super(props)

    this.state = {submittingBraintree: false, error: ''}

    this.handleBack = this.handleBack.bind(this)
    this.handleFinish = this.handleFinish.bind(this)
    this.handleBraintreeError = this.handleBraintreeError.bind(this)
    this.handleBraintreeSuccess = this.handleBraintreeSuccess.bind(this)
    this.updateProjectAssured = this.updateProjectAssured.bind(this)
    this.updateUser = this.updateUser.bind(this)
    this.updateInvoiceNote = this.updateInvoiceNote.bind(this)
    this.publishProject = this.publishProject.bind(this)
    this.handleSuccess = this.handleSuccess.bind(this)
  }

  redirectIfNeeded(project) {
    /* redirect to project page if not on draft status */
    if (project.status != 'draft' && !this.props.isProjectPage) {
      this.props.router.push(`/${project.subcategory}/${project.title}-${project.id}/`)
    }
  }

  componentDidMount() {
    const { categories } = this.props;
    this.props.fetchProjectIfNeeded({projectId: this.props.projectID})
      .then(({data}) => {
        const subcategory = getSubcategorySlug(categories, data.sub_category);
        const category = getCategory(categories.data, data.category);
        trackGoogle("pap", "started payment", subcategory, null, function() {
          trackPerfectAudience("started_payment");
          trackFacebookCustomEvent("1726897664301628", "started_payment");
          // this will be always called
          this.redirectIfNeeded(data)
        }.bind(this))
        trackMixpanel("Started PAP Payment", {
          categorySlug: category.slug,
          subCategorySlug: subcategory,
        });
      })
    this.props.fetchMethodIfNeeded()
    this.props.fetchLeadSourcesIfNeeded()
    this.props.fetchCountriesIfNeeded()
  }

  handleBack() {
    const { onBack, router, projectID, category, subCategory } = this.props

    trackClickWithMixpanel(MP_CHANGED_PAP_STEP_EVENT_NAME, {
      categorySlug: category.slug,
      subCategorySlug: subCategory.slug,
      fromStep: "payment",
      toStep: null,
      sectionName: "payment",
      fromIndex: 0,
      toIndex: null,
      questionsInSection: 1,
      direction: "backwards"
    });

    return onBack? onBack(): router.push(`/start-a-project/${projectID}/review/`)
  }

  handleFinish() {
    /* If user has no payment method defined, submit Braintree form first so
       that one is created. If it's successfull, `this.handleBraintreeSuccess` is
       invoked so that we can proceed with the publishing process. */


    trackClickWithMixpanel(MP_CHANGED_PAP_STEP_EVENT_NAME, {
      categorySlug: this.props.category.slug,
      subCategorySlug: this.props.subCategory.slug,
      fromStep: "payment",
      toStep: null,
      sectionName: "payment",
      fromIndex: 0,
      toIndex: null,
      questionsInSection: 1,
      direction: "forward"
    });

    trackClickWithMixpanel(MP_CHANGED_PAP_SECTION_EVENT_NAME, {
      categorySlug: this.props.category.slug,
      subCategorySlug: this.props.subCategory.slug,
      fromSectionName: "payment",
      toSectionName: null,
      direction: "forward"
    });

    if (!this.props.method) {
      this.setState({submittingBraintree: true, error: ''})
      this.refs.braintree_form.getWrappedInstance().submit()
    } else {
      this.refs.submit_btn.click()
    }
  }

  handleBraintreeError(err) {
    this.setState({submittingBraintree: false, error: err})
  }

  handleBraintreeSuccess() {
    this.refs.submit_btn.click()
    this.setState({submittingBraintree: false})
  }

  updateProjectAssured(data) {
    const { projectID, projectData, user, assureProjectRequest } = this.props
    return assureProjectRequest(data['assured_confirmed'], projectID, projectData.features, user)
  }

  publishProject() {
    const {
      projectID, projectData, categories, updateProjectRequest, category, user
    } = this.props;

    return updateProjectRequest(projectID, {status: 'open'}).then((data) => {
      try {
        const subcategory = getSubcategorySlug(categories, projectData.sub_category);
        const detailedCost = (projectData ?
          getDetailedCost(
            projectData.features, projectData.award_rules, {},
            projectData.one_to_one_negotiation_value, projectData.billables, user,
            projectData.package_tips
          ) : null
        );

        const total = detailedCost ? detailedCost.total : 0;
        trackGoogle("pap", "posted project", subcategory, total);
        trackAdWordsProjectPostedConversion(projectID, total);
        trackBing("pap", "posted project", subcategory, total);
        trackPerfectAudience("posted_project", {orderId: projectID, revenue: total });

        // track a few different things to allow for fullest set of analyses possible
        trackMixpanel("Posted project", {
          price: total,
          orderId: projectID,
          subcategorySlug: subcategory,
          category: category.slug,
        });
        trackChargeWithMixpanel(total);

        const categorySlugEvent = (category.slug || '').replace('1to1-', '').replace(/-/g, '_');
        trackGoogle("pap", `posted ${categorySlugEvent} project`, subcategory, total);
        trackPerfectAudience(
          `posted_${categorySlugEvent}_project`, {orderId: projectID, revenue: total }
        );
        trackFacebookCustomEvent(
          "1726897664301628", `posted_${categorySlugEvent}_project`, {orderID: projectID, revenue: total }
        );

        trackFacebookPurchase("1726897664301628", projectID, total);
        trackQuora();

      } catch (error) {
        console.error(error);
      }
      return data;
    });
  }

  updateUser(data) {
    const { updateUserRequest, user } = this.props
    return updateUserRequest(user.id, pick(data,
      ['lead_source', 'billing_name', 'billing_address_line_1', 'billing_address_line_2',
       'billing_city', 'billing_state', 'billing_postal_code', 'billing_country', 'phone_number'])
    ).then((res) => {
      setMixpanelProfile(res.data["email"], mixpanelProfileFromCsProfile(res.data));
    })
  }

  updateInvoiceNote(data) {
    const { projectID, updateProjectRequest } = this.props;

    if (!data.invoice_note) {
      return Promise.resolve();
    }

    return updateProjectRequest(projectID, { invoice_note: data.invoice_note });
  }

  handleSuccess({data}) {
    const { router, updateProjectLocal, onSuccess, categories, fetchCurrentUser } = this.props

    /* save project changes locally */
    updateProjectLocal(data)
    const subcategory = getSubcategorySlug(categories, data.sub_category);
    const url = getProjectUrl(data, subcategory);

    /* fetch updated user object with no stored discount codes */
    fetchCurrentUser();

    /* the state here is needed to display one-time-only stuff to the client on the project page */
    return router.push({
      pathname: url,
      state: {
        isSuccess: true
      }
    });
  }

  handleSubmit(data) {
    return this.updateProjectAssured(data)
      .then(() => this.updateUser(data))
      .then(() => this.updateInvoiceNote(data))
      .then(this.publishProject)
      .then(this.handleSuccess)
      .catch((res) => {
        this.setState({error: "Failed to publish project"});

        /* report error details to Sentry */
        Sentry.withScope((scope) => {
          Sentry.setExtra("response", res);
          Sentry.setExtra("step", "PAP payment");
          Sentry.captureMessage("Error occured while posting the project", "error");
        });
      })
  }

  render() {
    const {
      submitting, valid, projectData, handleSubmit, method, assured, subCategory,
      leadSources, countries, projectID, fetchingMethod, user, packageName
    } = this.props;
    const { submittingBraintree, error } = this.state
    const detailedCost = (projectData?
      getDetailedCost(
        projectData.features, projectData.award_rules, {},
        projectData.one_to_one_negotiation_value, projectData.billables, user,
        projectData.package_tips
      ) : null
    )
    const isTagline = subCategory.slug === 'business-tagline';
    const total = detailedCost? detailedCost.total : 0

    return <PAPBase
      sectionName="payment"
      extraClasses={[""]}
      loadingData={!projectData}
      currentStepIndex={0}
      currentSectionStepCount={0}
      onPrevStep={() => {}}
      onNextStep={() => {}}
      nextEnabled={false}
      prevEnabled={false}
      bottomNavHidden={true}
      {...this.props}>
      <div className="form-payment pap-form-step">
        <div className="row align-center">
          <div className={classnames("columns", {
             "medium-12 large-8": !this.props.isProjectPage,
             "small-16 medium-14": this.props.isProjectPage
          })}>
            <fieldset className="field payment-summary">
              <h3>Total Cost</h3>
              <h1 className="payment-total"><Money value={total}/></h1>
              <CostBreakdown detailedCost={detailedCost} packageName={this.props.packageName} showGrandTotal={false}/>
            </fieldset>
          </div>
        </div>
        <div className="row align-center">
          <div className={classnames("columns", {
            "medium-12 large-8": !this.props.isProjectPage,
             "small-16 medium-14": this.props.isProjectPage
          })}>
            <fieldset className="field">
              <h3>Payment Method</h3>

              {fetchingMethod?
              <LoadingIndicator fullScreen={false}/>:
              <div>
                {method?
                <div className="row columns">
                  <PaymentMethod />
                </div>:
                <BraintreeForm ref="braintree_form" onError={this.handleBraintreeError}
                  onSuccess={this.handleBraintreeSuccess}/>}
              </div>}

            </fieldset>
          </div>
        </div>
        <div className="row align-center">
          <div className={classnames("columns", {
             "medium-12 large-8": !this.props.isProjectPage,
             "small-16 medium-14": this.props.isProjectPage
          })}>
            <Form onSubmit={this.props.handleSubmit(this.handleSubmit.bind(this))}>

              <fieldset className="field">
                <h3>Billing Info</h3>
                <Field name="billing_name" component={TextInputField} label="Name"
                  validate={required}/>
                <Field name="billing_address_line_1" component={TextInputField}
                  label="Address 1" validate={required}/>
                <Field name="billing_address_line_2" component={TextInputField}
                  label="Address 2"/>
                <Field name="billing_city" component={TextInputField}
                  label="City" validate={required}/>
                <Field name="billing_state" component={TextInputField}
                  label="State" validate={required}/>
                <Field name="billing_country" component={SelectField} allowEmpty={true}
                  label="Country" validate={required} options={countries}/>
                <Field name="billing_postal_code" component={TextInputField}
                  label="ZIP" validate={required}/>
                <Field name="phone_number" component={PhoneInputField}
                  label="Phone" validate={[required, phone]}/>
              </fieldset>
              <fieldset className="field">
                <h3>Additional Invoice Details</h3>
                <p>
                  Your invoice will include your project name and your name and
                  email. If you need to include extra details on your paid
                  invoice, such as your company's name, address, purchase order, etc.,
                  please add those details here.
                </p>
                <Field name="invoice_note" component={TextAreaField} rows={5} />
              </fieldset>

              {!user['lead_source'] &&
              <fieldset className="field">
                <h3>How did you hear about us?</h3>
                <Field name="lead_source" component={SelectField} options={leadSources}
                       validate={required} allowEmpty={true} />
              </fieldset>}

              {/* Elite-only projects are always assured, but we DON'T require confirmation.
                  1-1 projects are always assured, but we DO require confirmation. */}
              {assured && !projectData.elite_only &&
              <fieldset className="field checkbox-field confirm-assured">
                {!projectData.is_one_to_one && <h3>Confirm Assured Award</h3>}
                {!projectData.is_one_to_one &&
                <div>
                  <p className="field-text">You selected to assure your award.</p>
                  <p className="disclaimer">
                    By selecting this option, you waive the option to request a refund. <br className="show-for-medium"/>Please confirm your selection.
                  </p>
                </div>}
                <label className="checkbox-label">
                  <Field name="assured_confirmed" component={CheckBoxField}
                   label={projectData.is_one_to_one?
                      `I understand and agree that ${projectData.mini? 'this' : 'a 1-to-1'} project is a contract directly between me and one creative and I waive my right to request a refund.`:
                      "Yes, I want to assure my award and waive the option to request a refund."}
                    validate={projectData.is_one_to_one? requiredConfirmation : undefined} />
                </label>
              </fieldset>}

              {!assured &&
              <fieldset className="field">
                <div className="payment-guarantee">
                  <h4>Work you&apos;ll love, guaranteed.</h4>
                  {projectData && projectData.entry_type === 'name'?
                  <p>
                    We want you to be thrilled with your project and are confident that you
                    will be! In {isTagline? 'tagline':'naming'} projects, we guarantee you will
                    receive at least 100 {isTagline? 'taglines':'names'}. If you receive fewer than
                    100 {isTagline? 'taglines':'names'} and you don't find one you like, we'll
                    issue a 100% refund. Simply send us a message within 30 days after your project
                    closes and we'll get right on it. Promise.
                  </p>:
                  <p>
                    If you are not completely satisfied with your project, contact us within 30 days
                    and we will promptly refund 100% of your money. Your happiness is our priority.
                  </p>}
                </div>
              </fieldset>}

              {projectData && projectData.elite_only &&
              <fieldset className="field">
                <div className="payment-guarantee">
                  <h4>Remember...</h4>
                  <p>
                    By working with the very best designers in your Elite project, you waive
                    your right to a refund and guarantee that each of the five designers
                    will be paid. The price you pay includes the award, tips, and our fee.
                  </p>
                </div>
              </fieldset>}

              {/* hidden button whose sole purpose is to allow submission from outside the form */}
              <button className="hide" ref="submit_btn"></button>
            </Form>
          </div>
        </div>
        <div className="row align-center">
          <div className={classnames("columns", {
             "small-16 medium-12 large-8": !this.props.isProjectPage,
             "small-16 medium-14": this.props.isProjectPage
          })}>
            <fieldset className="field payment-submit">
              {projectData && projectData.entry_type === 'name'?
              <p className="terms">
                { assured?
                'You have assured your project to increase participation and choice. By clicking the "Finish" button below, you agree that you cannot request a refund. ' : 'By clicking the "Finish" button below, you agree that if your project received over 100 entries, you cannot request a refund. ' }
                You also agree to
                our <Link to="/standards-of-conduct-client/" target="_blank">Standards of Conduct </Link>
                and our <Link to="/user-agreement/" target="_blank">User Agreement</Link>.
              </p>:
              <p className="terms">
                By clicking the "Finish" button below, you agree to
                our <Link to="/standards-of-conduct-client/" target="_blank">Standards of Conduct </Link>
                and our <Link to="/user-agreement/" target="_blank">User Agreement</Link>.
              </p>}

              <AnimatedButton
                processing={submittingBraintree || submitting}
                label={`Finish & Pay $${total.toFixed(2)}`}
                processingLabel="Processing..."
                successMsg="Done"
                onClick={this.handleFinish}
                errorMsg={error}
              />

              { !assured &&
              <img className="guarantee-img" src="https://s3.amazonaws.com/crowdspring3-assets/marketing/landing-page/crowdspring-landing-page-guarantee-hero.png" /> }

              { !valid &&
              <div className="text-danger form-error-msg">
                Please fix the errors above before proceeding.
              </div> }

              <button className="btn btn-text btn-with-icon back-btn" onClick={this.handleBack}>
                <i className="fa fa-angle-left"/> Back
              </button>
            </fieldset>
          </div>
        </div>
      </div>
    </PAPBase>
  }
}




// WEBPACK FOOTER //
// ./pap/components/payment.jsx